import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';

import { ReactComponent as ExcelDownloadIcon } from '@/pages/company/BankFeedRoute/components/ExportButton/excel-download-icon.svg';
import Tooltip from '@/components/mui/Tooltip';
import { useTriggerBankFeedTransactionsExportMutation } from '@/domain/bankFeed/queries';
import { useBankFeedRouteContext } from '@/pages/company/BankFeedRoute/context';
import { computeEmptyVariant } from '@/pages/company/BankFeedRoute/components/EmptyStates/computeEmptyVariant';
import { buildGridSnapshot } from '@/pages/company/BankFeedRoute/components/BankFeedGrid/buildGridSnapshot';
import { buildTransactionsDatePayload } from '@/pages/company/BankFeedRoute/components/FilterToolbar/dateRange';

const tooltipDescriptor = {
  id: 'company.bankFeed.transactions.toolbar.export.tooltip',
  defaultMessage: 'Export to Excel',
};

// Owns the full export lifecycle: derives in-progress / disabled state from
// the summary + mutation hook, builds the BE payload from the shared
// `gridApiRef` + toolbar filters at click time. IconButton's native `loading`
// prop (MUI v7) drives the spinner — no manual icon swap. Reads all
// parent-owned dependencies (gridApiRef / toolbarFilters / accountType /
// summary) from BankFeedRouteContext — no prop drilling and no own summary
// observer; render anywhere inside the route's provider tree.
const ExportButton: React.FC = () => {
  const intl = useIntl();
  const { gridApiRef, toolbarFilters, accountType, summary } = useBankFeedRouteContext();
  const triggerExport = useTriggerBankFeedTransactionsExportMutation();

  const isExportInProgress = summary?.exportInProgress === true || triggerExport.isLoading;
  const isEmptyView = computeEmptyVariant(summary, accountType) !== null;
  // `loading` already implies disabled — keep this for the other guards
  // (no data to export / currency not applied yet). On CARD the grid stays mounted but withholds its
  // datasource until a payment day is seeded (DA-19485); until then the shared date-payload builder
  // falls back to the Bank preset `dateRange`, so an export would diverge from the (still loading)
  // CARD grid. Disable export until `paymentDates` is non-empty.
  const isDisabled =
    isEmptyView ||
    toolbarFilters.currency == null ||
    (accountType === 'CARD' && toolbarFilters.paymentDates.length === 0);

  const handleClick = useCallback(() => {
    const api = gridApiRef.current;
    if (api == null || toolbarFilters.currency == null) return;
    // Mirror isDisabled: on CARD without a payment day the shared builder falls back to the Bank
    // preset dateRange, so guard here too — an export must never diverge from the CARD grid.
    if (accountType === 'CARD' && toolbarFilters.paymentDates.length === 0) return;
    const snapshot = buildGridSnapshot(api);
    triggerExport.mutate({
      body: {
        account_type: accountType,
        account_ids: toolbarFilters.accountIds,
        currency: toolbarFilters.currency,
        text: toolbarFilters.search.length > 0 ? [toolbarFilters.search] : [],
        filter_model: snapshot.filter_model,
        sort_model: snapshot.sort_model,
        headers: snapshot.headers,
        file_format: 'xlsx',
        ...buildTransactionsDatePayload(accountType, toolbarFilters),
      },
    });
  }, [accountType, gridApiRef, toolbarFilters, triggerExport]);

  return (
    <Tooltip t={tooltipDescriptor}>
      <IconButton
        color="primary"
        onClick={handleClick}
        disabled={isDisabled}
        loading={isExportInProgress}
        sx={{ ml: 'auto', p: 0.5 }}
        aria-label={intl.formatMessage(tooltipDescriptor)}
      >
        <SvgIcon component={ExcelDownloadIcon} inheritViewBox />
      </IconButton>
    </Tooltip>
  );
};

export default ExportButton;
